import { API_BASE_URL } from "../config/apiConfig";
import {
  Link,
  useNavigate,
} from "react-router-dom";

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaBriefcaseMedical,
  FaHeart,
  FaFileAlt,
  FaDollarSign,
  FaBell,
  FaCalendarAlt,
} from "react-icons/fa";

import calculateProfessionalStatus
  from "../utils/calculateProfessionalStatus";

import "../styles/pages/ProfessionalDashboard.css";

const API_BASE =
  API_BASE_URL;

/* ========================================
   TEMPORARY DASHBOARD DATA
======================================== */

const recentAssignments = [
  {
    title:
      "Emergency Registered Nurse",
    facility:
      "Regional Hospital",
    location:
      "Barrie, Ontario",
    status:
      "Open",
  },
  {
    title:
      "Intensive Care Registered Nurse",
    facility:
      "Northern Health Centre",
    location:
      "Sudbury, Ontario",
    status:
      "Urgent",
  },
  {
    title:
      "Registered Practical Nurse",
    facility:
      "Long-Term Care Home",
    location:
      "Toronto, Ontario",
    status:
      "Open",
  },
];

const notifications = [
  "New ICU assignment posted in Barrie.",
  "Your application has been reviewed.",
  "Your BLS certificate expires in 30 days.",
];

/* ========================================
   HELPERS
======================================== */

const getToken = () =>
  localStorage.getItem(
    "zethAuthToken"
  ) ||
  sessionStorage.getItem(
    "zethAuthToken"
  );

const parseJsonResponse =
  async (response) => {
    const contentType =
      response.headers.get(
        "content-type"
      );

    if (
      !contentType?.includes(
        "application/json"
      )
    ) {
      return {};
    }

    return response
      .json()
      .catch(() => ({}));
  };

const normalizeArray = (
  value
) =>
  Array.isArray(value)
    ? value
    : [];

/* ========================================
   PROFESSIONAL DASHBOARD
======================================== */

function ProfessionalDashboard() {
  const navigate =
    useNavigate();

  const [
    savedCount,
    setSavedCount,
  ] = useState(0);

  const [
    user,
    setUser,
  ] = useState(null);

  const [
    profile,
    setProfile,
  ] = useState(null);

  const [
    licences,
    setLicences,
  ] = useState([]);

  const [
    specialties,
    setSpecialties,
  ] = useState([]);

  const [
    documents,
    setDocuments,
  ] = useState([]);

  const [
    certifications,
    setCertifications,
  ] = useState([]);

  const [
    availability,
    setAvailability,
  ] = useState(null);

  const [
    isCheckingSession,
    setIsCheckingSession,
  ] = useState(true);

  const [
    showLoadingIndicator,
    setShowLoadingIndicator,
  ] = useState(false);

  /* ========================================
     LOAD DASHBOARD DATA
  ======================================== */

  useEffect(() => {
    async function verifySession() {
      const token =
        getToken();

      if (!token) {
        navigate(
          "/professional/login",
          {
            replace: true,
          }
        );

        return;
      }

      try {
        setIsCheckingSession(
          true
        );

        const requestOptions = {
          method: "GET",

          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        };

        const [
          sessionResponse,
          profileResponse,
          licenceResponse,
          specialtyResponse,
          documentsResponse,
          certificationsResponse,
          availabilityResponse,
        ] = await Promise.all([
          fetch(
            `${API_BASE}/auth/me`,
            requestOptions
          ),

          fetch(
            `${API_BASE}/profile`,
            requestOptions
          ),

          fetch(
            `${API_BASE}/professionals/licenses`,
            requestOptions
          ),

          fetch(
            `${API_BASE}/professionals/specialties`,
            requestOptions
          ),

          fetch(
            `${API_BASE}/professional/documents`,
            requestOptions
          ),

          fetch(
            `${API_BASE}/professionals/certifications`,
            requestOptions
          ),

          fetch(
            `${API_BASE}/professionals/availability`,
            requestOptions
          ),
        ]);

        const protectedResponses = [
          sessionResponse,
          profileResponse,
          licenceResponse,
          specialtyResponse,
          documentsResponse,
          certificationsResponse,
          availabilityResponse,
        ];

        const authenticationFailed =
          protectedResponses.some(
            (response) =>
              response.status ===
                401 ||
              response.status ===
                403
          );

        if (
          authenticationFailed
        ) {
          throw new Error(
            "Your session has expired."
          );
        }

        const [
          sessionData,
          profileData,
          licenceData,
          specialtyData,
          documentsData,
          certificationsData,
          availabilityData,
        ] = await Promise.all([
          parseJsonResponse(
            sessionResponse
          ),

          parseJsonResponse(
            profileResponse
          ),

          parseJsonResponse(
            licenceResponse
          ),

          parseJsonResponse(
            specialtyResponse
          ),

          parseJsonResponse(
            documentsResponse
          ),

          parseJsonResponse(
            certificationsResponse
          ),

          parseJsonResponse(
            availabilityResponse
          ),
        ]);

        if (
          !sessionResponse.ok
        ) {
          throw new Error(
            sessionData.message ||
              "Session verification failed."
          );
        }

        if (
          !profileResponse.ok
        ) {
          throw new Error(
            profileData.message ||
              "Unable to load your profile."
          );
        }

        if (
          !licenceResponse.ok
        ) {
          throw new Error(
            licenceData.message ||
              "Unable to load professional licences."
          );
        }

        if (
          !specialtyResponse.ok
        ) {
          throw new Error(
            specialtyData.message ||
              "Unable to load clinical specialties."
          );
        }

        if (
          !documentsResponse.ok
        ) {
          throw new Error(
            documentsData.message ||
              "Unable to load professional documents."
          );
        }

        if (
          !certificationsResponse.ok &&
          certificationsResponse.status !==
            404
        ) {
          throw new Error(
            certificationsData.message ||
              "Unable to load professional certifications."
          );
        }

        if (
          !availabilityResponse.ok &&
          availabilityResponse.status !==
            404
        ) {
          throw new Error(
            availabilityData.message ||
              "Unable to load availability."
          );
        }

        const loadedUser =
          sessionData.user ||
          sessionData.data ||
          null;

        const loadedProfile =
          profileData.user ||
          profileData.profile ||
          profileData.data ||
          null;

        setUser(
          loadedUser
        );

        setProfile(
          loadedProfile
        );

        setLicences(
          normalizeArray(
            licenceData.licenses ||
              licenceData.licences ||
              licenceData.data
          )
        );

        setSpecialties(
          normalizeArray(
            specialtyData.specialties ||
              specialtyData.data
          )
        );

        setDocuments(
          normalizeArray(
            documentsData.documents ||
              documentsData.data
          )
        );

        setCertifications(
          normalizeArray(
            certificationsData.certifications ||
              certificationsData.data
          )
        );

        setAvailability(
          availabilityResponse.ok
            ? (
                availabilityData.availability ||
                availabilityData.data ||
                null
              )
            : null
        );

        if (
          loadedUser
        ) {
          localStorage.setItem(
            "zethUser",
            JSON.stringify(
              loadedUser
            )
          );
        }
      } catch (error) {
        console.error(
          "Professional dashboard error:",
          error
        );

        localStorage.removeItem(
          "zethAuthToken"
        );

        localStorage.removeItem(
          "zethUser"
        );

        sessionStorage.removeItem(
          "zethAuthToken"
        );

        sessionStorage.removeItem(
          "zethUser"
        );

        navigate(
          "/professional/login",
          {
            replace: true,
          }
        );
      } finally {
        setIsCheckingSession(
          false
        );
      }
    }

    verifySession();
  }, [
    navigate,
  ]);

  /* ========================================
     DELAYED LOADING INDICATOR
  ======================================== */

  useEffect(() => {
    if (
      !isCheckingSession
    ) {
      setShowLoadingIndicator(
        false
      );

      return undefined;
    }

    const timer =
      window.setTimeout(
        () => {
          setShowLoadingIndicator(
            true
          );
        },
        300
      );

    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    isCheckingSession,
  ]);

  /* ========================================
     SAVED ASSIGNMENT COUNT
  ======================================== */

  useEffect(() => {
    function updateSavedCount() {
      const saved =
        JSON.parse(
          localStorage.getItem(
            "zethSavedAssignments"
          ) || "[]"
        );

      setSavedCount(
        Array.isArray(saved)
          ? saved.length
          : 0
      );
    }

    updateSavedCount();

    window.addEventListener(
      "storage",
      updateSavedCount
    );

    window.addEventListener(
      "zeth-saved-jobs-updated",
      updateSavedCount
    );

    return () => {
      window.removeEventListener(
        "storage",
        updateSavedCount
      );

      window.removeEventListener(
        "zeth-saved-jobs-updated",
        updateSavedCount
      );
    };
  }, []);

  /* ========================================
     PERSONAL INFORMATION COMPLETION
  ======================================== */

  const personalInformationComplete =
    useMemo(() => {
      if (!profile) {
        return false;
      }

      const firstName =
        profile.firstName ||
        profile.first_name;

      const lastName =
        profile.lastName ||
        profile.last_name;

      const phone =
        profile.phone ||
        profile.phone_number;

      const profession =
        profile.profession ||
        profile.profession_name;

      const province =
        profile.province ||
        profile.state;

      return Boolean(
        firstName &&
          lastName &&
          profile.email &&
          phone &&
          profession &&
          province
      );
    }, [
      profile,
    ]);

  /* ========================================
     PROFESSIONAL STATUS
  ======================================== */

  const professionalStatus =
    useMemo(() => {
      return calculateProfessionalStatus({
        personalInformationComplete,
        licences,
        specialties,
        certifications,
        documents,
        availability,
      });
    }, [
      personalInformationComplete,
      licences,
      specialties,
      certifications,
      documents,
      availability,
    ]);

  const profileCompletion =
    professionalStatus
      .profile
      .percentage;

  const assignmentReady =
    professionalStatus
      .assignment
      .ready;

  /* ========================================
     SUMMARY CARDS
  ======================================== */

  const summaryCards = [
    {
      icon:
        <FaBriefcaseMedical />,
      value:
        "24",
      label:
        "Available Assignments",
    },
    {
      icon:
        <FaHeart />,
      value:
        savedCount.toString(),
      label:
        "Saved Jobs",
    },
    {
      icon:
        <FaFileAlt />,
      value:
        "5",
      label:
        "Applications",
    },
    {
      icon:
        <FaDollarSign />,
      value:
        "$4,850",
      label:
        "Estimated Earnings",
    },
  ];

  /* ========================================
     LOADING
  ======================================== */

  if (
    isCheckingSession &&
    showLoadingIndicator
  ) {
    return (
      <div className="professional-dashboard">
        <div className="dashboard-main">
          <main className="dashboard-content">
            <div className="dashboard-inline-loading">
              <div className="dashboard-inline-spinner" />

              <span>
                Loading dashboard...
              </span>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (
    isCheckingSession
  ) {
    return null;
  }

  if (!user) {
    return null;
  }

  /* ========================================
     DISPLAY VALUES
  ======================================== */

  const firstName =
    user.first_name ||
    user.firstName ||
    profile?.first_name ||
    profile?.firstName ||
    "Professional";

  const lastName =
    user.last_name ||
    user.lastName ||
    profile?.last_name ||
    profile?.lastName ||
    "";

  const displayName =
    `${firstName} ${lastName}`
      .trim();

  const profession =
    user.profession ||
    user.profession_name ||
    profile?.profession ||
    profile?.profession_name ||
    "Healthcare Professional";

  const province =
    user.province ||
    profile?.province ||
    "";

  const country =
    user.country ||
    profile?.country ||
    "";

  /* ========================================
     DASHBOARD
  ======================================== */

  return (
    <div className="professional-dashboard">
      <div className="dashboard-main">

        {/* HEADER */}
        <header className="dashboard-topbar">
          <div>
            <p>
              Professional Portal
            </p>

            <h1>
              Good morning,{" "}
              {displayName} 👋
            </h1>

            <p>
              {profession}

              {province
                ? ` • ${province}`
                : ""}

              {country
                ? `, ${country}`
                : ""}
            </p>
          </div>

          <button
            className="dashboard-notification-button"
            type="button"
            aria-label="View notifications"
          >
            <FaBell />

            <span>
              {
                notifications.length
              }
            </span>
          </button>
        </header>

        <main className="dashboard-content">

          {/* PROFILE COMPLETION */}
          <section className="profile-progress-card">
            <div className="dashboard-progress-summary">
              <p>
                Profile completion
              </p>

              <strong>
                {profileCompletion}%
                Complete
              </strong>

              <small
                className={`dashboard-readiness-status ${
                  assignmentReady
                    ? "ready"
                    : "not-ready"
                }`}
              >
                {
                  professionalStatus
                    .assignment
                    .label
                }
              </small>
            </div>

            <div className="profile-progress-track">
              <span
                style={{
                  width:
                    `${profileCompletion}%`,
                }}
              />
            </div>

            <Link
              to="/professional/profile"
              className="dashboard-complete-profile-link"
            >
              {profileCompletion ===
              100
                ? "View your profile"
                : "Complete your profile"}
            </Link>
          </section>

          {/* SUMMARY CARDS */}
          <section className="dashboard-summary-grid">
            {summaryCards.map(
              (card) => (
                <article
                  className="dashboard-summary-card"
                  key={
                    card.label
                  }
                >
                  <div className="dashboard-summary-icon">
                    {
                      card.icon
                    }
                  </div>

                  <div>
                    <strong>
                      {
                        card.value
                      }
                    </strong>

                    <span>
                      {
                        card.label
                      }
                    </span>
                  </div>
                </article>
              )
            )}
          </section>

          {/* ASSIGNMENTS + NOTIFICATIONS */}
          <section className="dashboard-two-column">

            {/* RECENT ASSIGNMENTS */}
            <div className="dashboard-panel">
              <div className="dashboard-panel-heading">
                <div>
                  <p>
                    Opportunities
                  </p>

                  <h2>
                    Recent Assignments
                  </h2>
                </div>

                <Link
                  to="/professional/assignments"
                >
                  View all
                </Link>
              </div>

              <div className="dashboard-assignment-list">
                {recentAssignments.map(
                  (
                    assignment
                  ) => (
                    <article
                      className="dashboard-assignment-item"
                      key={
                        assignment.title
                      }
                    >
                      <div>
                        <h3>
                          {
                            assignment.title
                          }
                        </h3>

                        <p>
                          {
                            assignment.facility
                          }
                        </p>

                        <span>
                          {
                            assignment.location
                          }
                        </span>
                      </div>

                      <strong>
                        {
                          assignment.status
                        }
                      </strong>
                    </article>
                  )
                )}
              </div>
            </div>

            {/* NOTIFICATIONS */}
            <div className="dashboard-panel">
              <div className="dashboard-panel-heading">
                <div>
                  <p>
                    Updates
                  </p>

                  <h2>
                    Notifications
                  </h2>
                </div>
              </div>

              <div className="dashboard-notification-list">
                {notifications.map(
                  (
                    notification
                  ) => (
                    <div
                      className="dashboard-notification-item"
                      key={
                        notification
                      }
                    >
                      <FaBell />

                      <span>
                        {
                          notification
                        }
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          </section>

          {/* UPCOMING SHIFTS */}
          <section className="dashboard-panel">
            <div className="dashboard-panel-heading">
              <div>
                <p>
                  Schedule
                </p>

                <h2>
                  Upcoming Shifts
                </h2>
              </div>

              <FaCalendarAlt />
            </div>

            <div className="dashboard-shift-empty">
              <h3>
                No upcoming shifts
              </h3>

              <p>
                Confirmed assignments
                and shift schedules will
                appear here.
              </p>

              <Link
                className="primary-button"
                to="/professional/assignments"
              >
                Browse Assignments
              </Link>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

export default ProfessionalDashboard;
